/**
 * RateHistory — read-only timeline of an account's assignment history.
 *
 * Every rate/holder window over time, newest `effectiveFrom` first, so the
 * user can answer "what was the rate in the past?". The underlying data is
 * append-only: each change is a new dated window, so this is a pure audit
 * view — no edit affordances live here.
 *
 * Data sources:
 *   GET /api/accounts/:id/assignments → { assignments, activeAssignment }
 *   GET /api/parties?role=client      → Party[]  (clientPartyId → displayName)
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { api } from '../api';
import type { AccountAssignments, Assignment, Party } from '../api';
import { SectionCard } from '../ui/components';
import { Pill, Spinner } from '../ui/primitives';

export interface RateHistoryProps {
  readonly accountId: number;
}

/** Format a stored decimal rate string as a trimmed percent, or em-dash when null. */
const pct = (r: string | null): string =>
  r === null ? '—' : `${+(Number(r) * 100).toFixed(4)}%`;

export function RateHistory({ accountId }: RateHistoryProps) {
  const assignmentsQuery = useQuery({
    queryKey: ['account-assignments', accountId] as const,
    queryFn: () =>
      api.get<AccountAssignments>(`/api/accounts/${accountId}/assignments`),
  });

  const clientsQuery = useQuery({
    queryKey: ['parties', 'client'] as const,
    queryFn: () => api.get<Party[]>('/api/parties?role=client'),
  });

  // Map clientPartyId → displayName for holder labelling.
  const clientNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of clientsQuery.data ?? []) map.set(p.id, p.displayName);
    return map;
  }, [clientsQuery.data]);

  // Newest window first (descending effectiveFrom). Stable tie-break by id desc.
  const windows = useMemo<Assignment[]>(() => {
    const list = assignmentsQuery.data?.assignments ?? [];
    return [...list].sort((a, b) => {
      if (a.effectiveFrom !== b.effectiveFrom)
        return a.effectiveFrom < b.effectiveFrom ? 1 : -1;
      return b.id - a.id;
    });
  }, [assignmentsQuery.data]);

  if (assignmentsQuery.isLoading || clientsQuery.isLoading) {
    return (
      <SectionCard section="accounts" title="Rate history">
        <div className="flex items-center justify-center gap-2 py-8 text-small text-ink-muted">
          <Spinner />
          <span>Loading rate history…</span>
        </div>
      </SectionCard>
    );
  }

  if (windows.length === 0) {
    return (
      <SectionCard section="accounts" title="Rate history">
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <span className="text-sec-accounts opacity-90">
            <History className="h-8 w-8" strokeWidth={1.5} />
          </span>
          <p className="text-small text-ink-muted">No rate history</p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard section="accounts" title="Rate history" padded={false}>
      <ol className="flex flex-col gap-3 p-4">
        {windows.map((w) => {
          const isOpen = w.effectiveTo === null;
          const holder =
            w.clientPartyId === null
              ? 'Self-held'
              : clientNames.get(w.clientPartyId) ?? `Client #${w.clientPartyId}`;
          return (
            <li
              key={w.id}
              className="app-shell-surface-soft rounded-[20px] border border-border p-3.5 shadow-sm backdrop-blur"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-mono text-small font-semibold tabular-nums text-ink">
                    <span>{w.effectiveFrom}</span>
                    <span className="text-ink-muted">→</span>
                    <span>{w.effectiveTo ?? 'now'}</span>
                  </div>
                  <div className="mt-1 truncate text-caption text-ink-muted">
                    {holder}
                  </div>
                </div>
                {isOpen && (
                  <Pill section="accounts">current</Pill>
                )}
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-caption uppercase tracking-[0.1em] text-ink-muted">
                    Upline rate
                  </dt>
                  <dd className="mt-0.5 font-mono text-body font-semibold tabular-nums text-ink">
                    {pct(w.uplineRate)}
                  </dd>
                </div>
                <div>
                  <dt className="text-caption uppercase tracking-[0.1em] text-ink-muted">
                    Client rate
                  </dt>
                  <dd className="mt-0.5 font-mono text-body font-semibold tabular-nums text-ink">
                    {pct(w.clientRate)}
                  </dd>
                </div>
              </dl>
            </li>
          );
        })}
      </ol>
    </SectionCard>
  );
}
